import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { selectCurrentUserFullName, selectIsuserFullNameImpersonate } from '../../../pages/login/authSlice';
import { datetimeToDate,  getChangeDateFormate } from '../../../utils/dateFormat';
import { useCreateDelegateMutation, useGetAssociateDelegateQuery, useGetDelegateHistoryQuery } from '../delegateSlice';
import { useGetUserByNameQuery } from '../../../components/scoreCardFilter/scoreCardFilterSlice';

type delegateProps = {
    loggedEmpNum: string;
};

type Employee = [
    {
        name: string;
        id: string;
        supId: string;
    }
];

export const DelegateToForm = ({ loggedEmpNum }: delegateProps) => {
    const loggedEmpFullName = useSelector(selectCurrentUserFullName);
    const impEmpFullName = useSelector(selectIsuserFullNameImpersonate);
    const [delegateOracleID, setDelegateOracleID] = useState<string>('');
    const [endDate, setEndDate] = useState<any>('');
    const [startDate, setStartDate] = useState<Date>(new Date());
    const [errorDelegateIdMessage, setErrorDelegateIdMessage] = useState<string>();
    const [errorAssociateMessage, setErrorAssociateMessage] = useState<string>();
    const [errorEndDateMessage, setErrorEndDateMessage] = useState<string>();
    const [listOfUsers, setListOfUsers] = useState<Employee>([{ name: '', id: '', supId: '' }]);
    const [disableSave, setDisableSave] = useState<boolean>(true);

    const [selectedAssociate, setSelectedAssociate] = useState<any>('');
    const [activateDelegate] = useCreateDelegateMutation();
    const [delegateEmployeeName, setDelegateEmployeeName] = useState<string>('');
    const [inputNameOrID, setInputNameOrID] = useState<any>([{ valName: '', valId: '', dataType: 0 }]);
    const [errorDelegateEmployeeNameMessage, setErrorDelegateEmployeeNameMessage] = useState<string>();
    const [formTouched, setFormTouched] = useState(false);


    const { data: associateDelegateData } = useGetAssociateDelegateQuery(
        {
            employeenumber: loggedEmpNum,
            delegatenumber: delegateOracleID,
        },
        { skip: delegateOracleID.length < 7 || loggedEmpNum === delegateOracleID }
    );

    const {
        data: delegateEmployeeNameData,
        isSuccess: nameFound,
    } = useGetUserByNameQuery(
        {
            filterParam: delegateEmployeeName,
            searcherEmpNo: loggedEmpNum,
            getAllEmps: true,
        },
        { skip: !delegateEmployeeName || !loggedEmpNum }
    );

    const { data: delegationHistory } = useGetDelegateHistoryQuery({
        employeenumber: loggedEmpNum,
        isDelegateTo: true,
    });


    function isSameDelegationsExists(delegateEmployeeNumber: string, startDate: string, endDate: string) {
        return delegationHistory?.data?.some((item: any) => {
            return (
                item.delegate_employee_number === delegateEmployeeNumber &&
                item?.start_date?.split(' ')[0] === startDate &&
                item?.end_date?.split(' ')[0] === endDate
            );
        });
    }

    const activateDelegateclick = () => {
        if (isSameDelegationsExists(delegateOracleID, getChangeDateFormate(startDate), getChangeDateFormate(endDate))) {
            toast.warning("Same date already exist!");
        } else {
          const strD=  datetimeToDate(startDate);
          const endD=  datetimeToDate(endDate);

            const data = {
                delegatorEmployeeNumber: parseInt(loggedEmpNum, 10),
                delegateEmployeeNumber: parseInt(selectedAssociate?.id, 10),
                startDate:strD,
                endDate:endD,
            };
            activateDelegate(data)
                .unwrap()
                .then((res: any) => {
                    setDisableSave(true);
                    if (res.message === 'Success') toast.success('Save Success');
                    else toast.error('Save Failed');
                    setDelegateOracleID('');
                })
                .then((error: any) => {
                    console.log(error);
                    setDisableSave(true);
                });
        }
    };

    const validateForm = (): boolean => {
        if (loggedEmpNum === delegateOracleID) {
            toast.error('Self delegation is not allowed');
            return false;
        } else setErrorDelegateIdMessage('');
        if (listOfUsers.length > 0 && !selectedAssociate?.id) {
            setErrorAssociateMessage('Please select Employee');
            return false;
        } else setErrorAssociateMessage('');
        if (endDate === '' || endDate === undefined) {
            setErrorEndDateMessage('Please select End Date');
            return false;
        } else setErrorEndDateMessage('');
        if (endDate < startDate) {
            setErrorEndDateMessage('End Date should be equal or greater than start date');
            return false;
        } else setErrorEndDateMessage('');

        return true;
    };

    const validateEndDate = (e: any) => {
        const date = new Date(e.value);
        date.setHours(23, 59, 59);
        setEndDate(date);
    };

    useEffect(() => {
        if (delegateEmployeeNameData?.data.length > 0) {
            const arrayOfUsers: Employee = delegateEmployeeNameData.data.map((item: any) => ({
                name: item?.first_name.concat(' ', item?.last_name, ' (', item?.employee_number, ')'),
                id: item?.employee_number,
                supId: item?.supervisor_number,
            }));
            setListOfUsers(arrayOfUsers);
            setSelectedAssociate('');
        }
    }, [delegateEmployeeNameData, nameFound]);


    useEffect(() => {
        if (inputNameOrID.dataType === 1) {
            setDelegateEmployeeName('');
            setListOfUsers([{ name: '', id: '', supId: '' }]);
        }

        if (inputNameOrID.dataType === 2) {
            const name = inputNameOrID.valName?.trim();
            if (name && name.length >= 3) {
                setDelegateEmployeeName(name);
            } else {
                setListOfUsers([{ name: '', id: '', supId: '' }]);
            }
        }
    }, [inputNameOrID.valName, inputNameOrID.dataType]);

    useEffect(() => {
        setSelectedAssociate('');
        if (associateDelegateData?.data.length > 0) {
            const arrayOfUsers: Employee = associateDelegateData?.data.map((item: any) => ({
                name: item?.employee_name.concat(' (', item?.employee_number, ')'),
                id: item?.employee_number,
            }));
            setListOfUsers(arrayOfUsers);
        } else if (associateDelegateData?.data.length === 0 && delegateOracleID.length > 0) {
            toast.error('Oracle ID is not authorized to delegate');
            setDisableSave(true);
        }
    }, [associateDelegateData]);

    useEffect(() => {
        if (formTouched) {
            const isformValid = validateForm();
            setDisableSave(!isformValid);
        }
    }, [
        formTouched,
        delegateOracleID,
        selectedAssociate,
        endDate,
        startDate,
        delegateEmployeeName,
    ]);


 return (
    <div>
            {' '}
            <div className="row mt-4">
            <div className="col-md-6">
                <h6>From</h6>
                <InputText
                    disabled
                    className="borderRadius10 w-100"
                    placeholder={`${loggedEmpFullName === undefined ? impEmpFullName : loggedEmpFullName} (${loggedEmpNum})`}
                />
            </div>
        </div>
         <div className="row mt-4">
             <div className="col-md-2">
                 <h6>Oracle ID</h6>
                 <InputText
                     value={delegateOracleID}
                     onChange={(e: any) => {
                         setDelegateOracleID(e.target.value);
                         setInputNameOrID({ valName: '', valId: '', dataType: 0 });
                         setDelegateEmployeeName('');
                         setErrorDelegateEmployeeNameMessage('');
                         setFormTouched(true);
                     }}
                     placeholder="Enter Oracle ID"
                     keyfilter="int"
                     className={`borderRadius10 w-100 ${errorDelegateIdMessage ? 'p-invalid' : ''}`}
                 />
                 <span className="error-text">{errorDelegateIdMessage}</span>
             </div>

             <div className="col-md-4">
                 <h6>Employee Name</h6>
                 <InputText
                     className={`borderRadius10 w-100 ${errorDelegateEmployeeNameMessage ? 'p-invalid' : ''}`}
                     placeholder="Enter Employee Name"
                     keyfilter={/[a-zA-Z\sa-zA-Z]/}
                     value={inputNameOrID.valName || ''}
                     onChange={(e) => {
                         setInputNameOrID({
                             valName: e.target.value,
                             dataType: 2,
                         });
                         setDelegateOracleID('');
                         setListOfUsers([{ name: '', id: '', supId: '' }]);
                         setSelectedAssociate('');
                         setErrorDelegateIdMessage('');
                         setFormTouched(true);
                     }}
                 />
                 <span className="error-text">{errorDelegateEmployeeNameMessage}</span>
             </div>

             <div className="col-md-6">
                 <h6>To</h6>
                 <Dropdown
                     value={selectedAssociate}
                     className={`custom-dropdown w-100 ${formTouched && errorAssociateMessage ? 'p-invalid' : ''}`}
                     options={listOfUsers}
                     onChange={(e: any) => {
                         setSelectedAssociate(e.value);
                         setFormTouched(true);
                     }}
                     optionLabel="name"
                     placeholder="Select Employee"
                 />
                 <span className="error-text">{listOfUsers.length > 0 ? errorAssociateMessage : ''}</span>
             </div>
         </div>

         <div className="row mt-4">
             <div className="col-md-6">
                 <h6>Start</h6>
                 <Calendar
                     id="startDate"
                     value={startDate}
                     onChange={(e: any) => setStartDate(e.value)}
                     className="custom-calendar w-100"
                     showIcon
                     placeholder="Select Start Date"
                     dateFormat="dd M yy"
                 />
             </div>

             <div className="col-md-6">
                 <h6>End</h6>
                 <Calendar
                     id="endDate"
                     value={endDate}
                     onChange={(e: any) => validateEndDate(e)}
                     showIcon
                     placeholder="Select End Date"
                     dateFormat="dd M yy"
                     minDate={new Date()}
                     className={`custom-calendar w-100 ${formTouched && errorEndDateMessage ? 'p-invalid' : ''}`}
                 />
                 <span className="error-text" id="errMessage">
                     {errorEndDateMessage}
                 </span>
             </div>
         </div>

           <div className='row'>
                <div className='col-12 right-align'>
                    <Button
                        disabled={disableSave}
                        label="Activate"
                        onClick={activateDelegateclick}
                        className="btn-primary float-end mt-3"
                    />
                </div>
            </div>
    </div>
    );
};